import { AsyncPipe } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { AsmComponentService, AsmMainUiComponent } from '@spartacus/asm/components';
import { AsmService } from '@spartacus/asm/core';
import { AsmAuthStorageService, CsAgentAuthService, TokenTarget } from '@spartacus/asm/root';
import { AuthService, GlobalMessageService, GlobalMessageType, RoutingService, TranslatePipe } from '@spartacus/core';
import { LaunchDialogService } from '@spartacus/storefront';
import { UserAccountFacade } from '@spartacus/user/account/root';
import { Observable, of } from 'rxjs';
import { filter, shareReplay, switchMap, take } from 'rxjs/operators';
import { KnBrAuthRedirectService } from 'src/feature-libs/kn-br-user/kn-br-account/root/facade/kn-br-auth-redirect.service';
import { KnBrCsAgentAuthService } from '../../../../app/services/kn-br-csagent-auth.service';
import { environment } from '../../../../environments/environment';
import { KnBrAsmSessionTimerComponent } from '../kn-br-asm-session-timer/kn-br-asm-session-timer.component';
import { KnBrAsmToggleUiComponent } from '../kn-br-asm-toggle-ui/kn-br-asm-toggle-ui.component';
import { KnBrCSAgentLoginFormComponent } from '../kn-br-csagent-login-form/kn-br-csagent-login-form.component';
import { KnBrCustomerEmulationComponent } from '../kn-br-customer-emulation/kn-br-customer-emulation.component';
import { KnBrCustomerSelectionComponent } from '../kn-br-customer-selection/kn-br-customer-selection.component';

@Component({
  selector: 'cx-asm-main-ui',
  templateUrl: './kn-br-asm-main-ui.component.html',
  styleUrls: ['./kn-br-asm-main-ui.component.scss'],
  imports: [
    KnBrAsmToggleUiComponent,
    KnBrAsmSessionTimerComponent,
    KnBrCustomerEmulationComponent,
    KnBrCustomerSelectionComponent,
    KnBrCSAgentLoginFormComponent,
    AsyncPipe,
    TranslatePipe,
  ],
})
export class KnBrAsmMainUiComponent extends AsmMainUiComponent implements OnInit {
  startingCustomerSession = false;
  shouldShowAsm = false;
  customerSupportAgentLoggedIn$: Observable<boolean>;
  isCustomerEmulationSessionInProgress$: Observable<boolean>;

  constructor(
    protected authService: AuthService,
    protected csAgentAuthService: CsAgentAuthService,
    protected knbrcsAgentAuthService: KnBrCsAgentAuthService,
    protected asmAuthStorageService: AsmAuthStorageService,
    protected userService: UserAccountFacade,
    protected asmComponentService: AsmComponentService,
    protected globalMessageService: GlobalMessageService,
    protected routingService: RoutingService,
    protected asmService: AsmService,
    protected knBrAuthRedirectService: KnBrAuthRedirectService,
    protected launchDialogService: LaunchDialogService
  ) {
    knbrcsAgentAuthService.handleOAuthCallbackIfActive();

    super(
      authService,
      csAgentAuthService,
      asmComponentService,
      globalMessageService,
      routingService,
      asmService,
      userService,
      launchDialogService
    );

    this.customerSupportAgentLoggedIn$ = this.knbrcsAgentAuthService
      .isCustomerSupportAgentLoggedIn()
      .pipe(shareReplay({ bufferSize: 1, refCount: true }));

    this.isCustomerEmulationSessionInProgress$ = this.asmComponentService.isCustomerEmulationSessionInProgress();

    const urlParams = new URLSearchParams(window.location.search);
    const hasAsmParam = urlParams.has('asm');
    const hasCodeParam = urlParams.has('code');
    const isNormalCustomer =
      urlParams.get('client_id') === environment.AUTHENTICATION_CLIENT_ID;
    const asmModeWasActive = sessionStorage.getItem('asm_mode') === 'true';
    console.log('asm Mode', isNormalCustomer, asmModeWasActive);

    if (isNormalCustomer) {
      sessionStorage.removeItem('asm_mode');
      localStorage.removeItem('asm_token_target');
      localStorage.removeItem('asm_mode_fallback');
      this.shouldShowAsm = false;
    } else if (hasAsmParam) {
      sessionStorage.setItem('asm_mode', 'true');
      this.shouldShowAsm = true;
      this.knbrcsAgentAuthService.authorizeCustomerSupportAgentWhenUseCodeFlow();
    } else if (hasCodeParam && asmModeWasActive) {
      this.shouldShowAsm = true;
      this.asmAuthStorageService.setTokenTarget(TokenTarget.CSAgent);
      this.asmAuthStorageService.switchTokenTargetToCSAgent();
    } else {
      const asmEnabled = localStorage.getItem('asm_enabled') === 'true';

      if (asmEnabled) {
        this.shouldShowAsm = true;
      } else {
        this.shouldShowAsm = false;
      }
    }

    this.customerSupportAgentLoggedIn$.subscribe((v) => console.log('[ASM AGENT LOGIN]', v));

    this.isCustomerEmulationSessionInProgress$ = this.asmComponentService.isCustomerEmulationSessionInProgress();

    this.asmComponentService.isCustomerEmulationSessionInProgress().subscribe((v) => {
      this.startingCustomerSession = v;
    });
    this.asmComponentService.isCustomerEmulationSessionInProgress().subscribe(console.log);
  }

  ngOnInit(): void {
    const urlParams = new URLSearchParams(window.location.search);
    const isNormalCustomer =
      urlParams.get('client_id') === environment.AUTHENTICATION_CLIENT_ID;

    if (!this.shouldShowAsm || isNormalCustomer) {
      return;
    }

    this.asmAuthStorageService
      .getToken()
      .pipe(take(1))
      .subscribe((token) => {
        if (token && token.access_token) {
          this.asmAuthStorageService.setTokenTarget(TokenTarget.CSAgent);
          this.asmAuthStorageService.switchTokenTargetToCSAgent();
        }
      });

    super.ngOnInit();
    this.customer$ = this.authService.isUserLoggedIn().pipe(
      switchMap((isLoggedIn) => {
        if (isLoggedIn) {
          this.handleCustomerSessionStartRedirection();
          return this.userService.get();
        } else {
          return of(undefined);
        }
      })
    );

    this.authService.isUserLoggedIn().pipe(
      filter(Boolean),
      take(1)
    ).subscribe(() => {
      const urlParams = new URLSearchParams(window.location.search);
      const hasCode = urlParams.has('code');
      if (hasCode) {
        return;
      }
      this.knbrcsAgentAuthService.initCsAgentContext();
    });
  }

  protected override handleCustomerSessionStartRedirection(): void {
    this.asmComponentService
      .isCustomerEmulationSessionInProgress()
      .pipe(take(1))
      .subscribe((isCustomerEmulated) => {
        if (this.startingCustomerSession && isCustomerEmulated) {
          this.startingCustomerSession = false;
          this.globalMessageService.remove(GlobalMessageType.MSG_TYPE_ERROR);
          this.knBrAuthRedirectService.redirect();
        } else {
          this.startingCustomerSession = false;
        }
      });
  }

  override startCustomerEmulationSession({ customerId }: { customerId: string }): void {
    if (customerId) {
      this.startingCustomerSession = true;
      this.knbrcsAgentAuthService.startCustomerEmulationSession(customerId);
    } else {
      this.globalMessageService.add({ key: 'asm.error.noCustomerId' }, GlobalMessageType.MSG_TYPE_ERROR);
    }
  }

  override logout(...args: any[]): void {
    localStorage.removeItem('asm_enabled');
    localStorage.removeItem('asm_token_target');
    localStorage.removeItem('asm_mode_fallback');
    sessionStorage.removeItem('sloan_handshake_attempted');
    sessionStorage.removeItem('asm_mode');
    this.asmAuthStorageService.clearEmulatedUserToken();
    this.knbrcsAgentAuthService.logoutCustomerSupportAgent();
  }
}
